import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import "./App.css";
import SourceQuickAccess from "./components/source/SourceQuickAccess";
import SourcePanel from "./components/source/SourcePanel";
import Ticker from "./components/ticker/Ticker";
import TickerMostVisitedBar from "./components/ticker/TickerMostVisitedBar";
import BottomFloatingCapsule from "./components/floating/BottomFloatingCapsule";
import FloatingNewsCard from "./components/floating/FloatingNewsCard";
import NewsCard from "./components/news/NewsCard";
import FirstAdCard from "./components/ads/FirstAdCard";
import SharedNewsModal from "./components/modal/SharedNewsModal";
import FocusPager from "./components/pager/FocusPager";
import { FocusPagerSkeleton } from "./components/pager/FocusPagerPage";
import VirtualizedNewsList, { NewsCardLoadSkeleton } from "./components/news/VirtualizedNewsList";
import MostVisitedPager from "./components/pager/MostVisitedPager";
import SavedPager from "./components/pager/SavedPager";
import LoadingTicker from "./components/skeleton/LoadingTicker";
import AppSkeleton from "./components/skeleton/AppSkeleton";
import { toPersianDigits } from "./utils/toPersianDigits";
import {
  compareNewsByPublished,
  sortNewsByPublished,
} from "./utils/newsSort";
import {
  getSourceKey,
  getSourceMapValue,
  isSameSource,
  sortSourceListAlphabetically,
} from "./utils/sourceModel";
import TickerSavedBar, { SAVED_FILTERS } from "./components/ticker/TickerSavedBar";
import {
  NEWS_CONFIG,
  MOST_VISITED_FILTERS,
} from "./config/newsConfig";
import { recordNewsViewRequest } from "./api/newsApi";
import { useNewsFeed } from "./hooks/useNewsFeed";
import { useSources } from "./hooks/useSources";
import { useSavedNews } from "./hooks/useSavedNews";
import { useMostVisitedNews } from "./hooks/useMostVisitedNews";
import { useSharedNews } from "./hooks/useSharedNews";
import { useUnreadCounts } from "./hooks/useUnreadCounts";
import { useScrollState } from "./hooks/useScrollState";
import { useAppModes } from "./hooks/useAppModes";
import { useMediaQuery } from "./hooks/useMediaQuery";
import { useFavicons } from "./hooks/useFavicons";
import { useQuickAccessSources } from "./hooks/useQuickAccessSources";
import RetryLogoImage from "./components/media/RetryLogoImage";
import useInitialFeedVisualReady from "./hooks/useInitialFeedVisualReady";
import useAdPlan from "./hooks/useAdPlan";
import usePopupNotice from "./hooks/usePopupNotice";
import PopupNotice from "./components/popup/PopupNotice";
import {
  getLatestNewsCursor,
  getNewsKey,
  mergeUniqueNewsByPublished,
} from "./utils/newsModel";

const SAVED_CATEGORY_FILTERS = SAVED_FILTERS.map(({ id }) => id);
const OFFLINE_ERROR_MESSAGE = "اتصال اینترنت خود را بررسی کنید";

function App() {
  const {
    allNews,
    setAllNews,
    loading,
    error,
    refreshError,
    isBrowserOffline,
    sourceTotalCounts,
    unreadFallbackCounts,
    unreadCountSince,
    sourceCatalog,
    availableSources,
    pendingNews,
    setPendingNews,
    largeBacklog,
    homeSnapshotRefreshing,
    clearLargeBacklog,
    setInsertPollingContext,
    replaceHomeFeedWithLatestSnapshot,
    commitPendingNewsToUnread,
    commitPendingCountsToUnread,
    focusNewsBySource,
    hasMoreHomeNewsForSources,
    canAttemptHomeLoadMoreForSources,
    hasMoreNewsBySource,
    checkedFocusSources,
    failedFocusSources,
    loadingFocusSources,
    fetchOlderNews,
    seedFocusSourceNews,
    fetchFocusSourceNews,
    retryFocusSourceNews,
  } = useNewsFeed();
  const {
    activeSources,
    sourceSelectionRevision,
    sourceSearchTerm,
    setSourceSearchTerm,
    isSourcePanelOpen,
    setIsSourcePanelOpen,
    handleToggleSource,
    toggleSourcePanel,
  } = useSources({ availableSources, sourceCatalog });

  const favicons = useFavicons();
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [mainListTarget, setMainListTarget] = useState(null);
  const [homePreparationCycle, setHomePreparationCycle] = useState(0);
  const [isPreparingHomeInitialBatch, setIsPreparingHomeInitialBatch] =
    useState(false);
  const [hasCompletedInitialAppReveal, setHasCompletedInitialAppReveal] =
    useState(false);
  const [hasInitialSkeletonMinimumElapsed, setHasInitialSkeletonMinimumElapsed] =
    useState(() => Boolean(window.__CHEKHABARE_LAUNCH_SCREEN_HIDDEN__));
  const [deepestHomeReadNews, setDeepestHomeReadNews] = useState(null);

  const homePreparationInFlightRef = useRef(false);
  const homeScrollTopRef = useRef(0);
  const lastRefreshedSourceSelectionRevisionRef = useRef(0);

  const {
    focusSource,
    setFocusSource,
    isMostVisitedMode,
    setIsMostVisitedMode,
    isSavedMode,
    setIsSavedMode,
    savedFilter,
    setSavedFilter,
  } = useAppModes();

  const isMobile = useMediaQuery("(max-width: 767px)");

  /*
  |-------------------------------------------------------------------------
  | Mobile Most Visited source commit
  |-------------------------------------------------------------------------
  |
  | Source toggles inside the mobile Source Panel are a draft selection.
  | Most Visited keeps using the last committed selection while the panel is
  | open, so tapping several + / - controls does not launch three requests per
  | tap. Closing the panel commits the final selection exactly once.
  |
  | Desktop intentionally keeps the live behavior because the Source Panel
  | and Most Visited content are visible together there.
  |
  */
  const [mobileCommittedMostVisitedSources, setMobileCommittedMostVisitedSources] =
    useState(activeSources);

  const activeSourcesSelectionKey = useMemo(() => {
    return sortSourceListAlphabetically(activeSources || [])
      .map(getSourceKey)
      .filter(Boolean)
      .join("||");
  }, [activeSources]);

  const mobileCommittedMostVisitedSourcesKey = useMemo(() => {
    return sortSourceListAlphabetically(
      mobileCommittedMostVisitedSources || []
    )
      .map(getSourceKey)
      .filter(Boolean)
      .join("||");
  }, [mobileCommittedMostVisitedSources]);

  useEffect(() => {
    if (!isMobile || isSourcePanelOpen || activeSources === null) {
      return;
    }

    if (
      activeSourcesSelectionKey ===
      mobileCommittedMostVisitedSourcesKey
    ) {
      return;
    }

    setMobileCommittedMostVisitedSources(activeSources);
  }, [
    activeSources,
    activeSourcesSelectionKey,
    isMobile,
    isSourcePanelOpen,
    mobileCommittedMostVisitedSourcesKey,
  ]);

  const mostVisitedActiveSources = useMemo(() => {
    if (!isMobile) {
      return activeSources;
    }

    if (isSourcePanelOpen) {
      return mobileCommittedMostVisitedSources ?? activeSources;
    }

    if (
      activeSourcesSelectionKey ===
      mobileCommittedMostVisitedSourcesKey
    ) {
      return mobileCommittedMostVisitedSources ?? activeSources;
    }

    return activeSources;
  }, [
    activeSources,
    activeSourcesSelectionKey,
    isMobile,
    isSourcePanelOpen,
    mobileCommittedMostVisitedSources,
    mobileCommittedMostVisitedSourcesKey,
  ]);

  const handleSourcePanelToggle = useCallback(() => {
    if (isMobile && !isSourcePanelOpen) {
      setMobileCommittedMostVisitedSources(activeSources);
    }

    toggleSourcePanel();
  }, [
    activeSources,
    isMobile,
    isSourcePanelOpen,
    toggleSourcePanel,
  ]);

  const {
    mainScrollParent,
    contentScrollParent,
    setMainElement,
    setContentWrapperElement,
    isAtTop,
    scrollToTop,
    scrollToNewsItem,
    scrollAppToTop,
    rememberMostVisitedScroll,
    restoreMostVisitedScroll,
    rememberSavedScroll,
    restoreSavedScroll,
    focusScrollToTopSignal,
    isFocusPageAtTop,
    setIsFocusPageAtTop,
    mostVisitedScrollToTopSignal,
    isMostVisitedPageAtTop,
    setIsMostVisitedPageAtTop,
    savedScrollToTopSignal,
    isSavedPageAtTop,
    setIsSavedPageAtTop,
    setSavedScrollToTopSignal,
  } = useScrollState({
    isMobile,
    focusSource,
    isMostVisitedMode,
    isSavedMode,
  });

  const {
    savedLinks,
    savedNews,
    savedPages,
    activeSavedItems,
    toggleSavedNews,
    clearSavedLinks,
  } = useSavedNews({
    allNews,
    pendingNews,
    focusNewsBySource,
    categoryFilters: SAVED_CATEGORY_FILTERS,
    activeFilter: savedFilter,
  });

  const {
    mostVisitedPages,
    mostVisitedLoadingByFilter,
    mostVisitedErrorsByFilter,
    isMostVisitedLoading,
    isActiveMostVisitedLoading,
    mostVisitedError,
    mostVisitedTarget,
    setMostVisitedTarget,
    mostVisitedFilter,
    setMostVisitedFilter,
    handleMostVisitedFilterChange: updateMostVisitedFilter,
    activeMostVisitedItems,
    hasLoadedActiveMostVisited,
    hasLoadedAllMostVisited,
    hasLoadedMostVisitedFilter,
    priorityMostVisitedTickerNews,
  } = useMostVisitedNews({
    activeSources: mostVisitedActiveSources,
    availableSourcesLength: availableSources.length,
    focusSource,
    isMostVisitedMode,
    isMobile,
  });

  const sharedNewsPool = useMemo(() => {
    const seen = new Set();
    const items = [];

    [
      ...allNews,
      ...pendingNews,
      ...Object.values(focusNewsBySource).flat(),
      ...Object.values(mostVisitedPages).flat(),
      ...savedNews,
    ].forEach((item) => {
      const id = item?.id;
      const key = id !== undefined && id !== null ? `id:${id}` : item?.link;

      if (!key || seen.has(key)) return;

      seen.add(key);
      items.push(item);
    });

    return items;
  }, [
    allNews,
    focusNewsBySource,
    mostVisitedPages,
    pendingNews,
    savedNews,
  ]);

  const {
    sharedNewsId,
    sharedNews,
    sharedNewsError,
    shouldShowSharedNewsModal,
    showSharedNewsLoadingTicker,
    sharedNewsSuppressViewIncrement,
    closeSharedNews,
    handleShareNews,
    handleViewNews,
  } = useSharedNews({
    newsPool: sharedNewsPool,
    loading,
    contentScrollParent,
    mainScrollParent,
  });

  const {
    markAsRead,
    markItemsAsRead,
    registerCommittedUnreadItems,
    registerSuppressedBackfillCounts,
    handleBottomMarkAllRead,
    unreadCounts,
    totalUnreadCount,
    focusedUnreadCount,
  } = useUnreadCounts({
    allNews,
    focusNewsBySource,
    focusSource,
    activeSources,
    availableSources,
    sourceTotalCounts,
    unreadFallbackCounts,
    unreadCountSince,
  });

  const handleHomeNewsVisible = useCallback(
    (news) => {
      markAsRead(news);

      setDeepestHomeReadNews((previous) => {
        if (!previous) return news;

        return compareNewsByPublished(news, previous) > 0
          ? news
          : previous;
      });
    },
    [markAsRead]
  );

  const loadMoreTimeoutRef = useRef(null);
  useEffect(() => {
    return () => {
      if (loadMoreTimeoutRef.current) {
        window.clearTimeout(loadMoreTimeoutRef.current);
      }
    };
  }, []);

  const clearSavedNews = useCallback(() => {
    clearSavedLinks();
    setIsSavedMode(false);
    setSavedFilter("all");
    restoreSavedScroll();
  }, [
    clearSavedLinks,
    restoreSavedScroll,
    setIsSavedMode,
    setSavedFilter,
  ]);

  const recordNewsView = useCallback((news) => {
    const id = news?.id;
    if (id === undefined || id === null) return;

    recordNewsViewRequest(id).catch(() => {});
  }, []);

  const openMostVisitedNewsWithoutRecording = useCallback(
    (news) => {
      markAsRead(news);
    },
    [markAsRead]
  );

  const handleMostVisitedViewNews = useCallback(
    (news, viewContext = {}) => {
      handleViewNews(news, {
        ...viewContext,
        suppressViewIncrement: true,
      });
    },
    [handleViewNews]
  );

  const openMostVisitedMode = useCallback(
    (payload) => {
      const clickedLink = payload?.clickedItem?.link;

      if (clickedLink) {
        setMostVisitedTarget({
          link: clickedLink,
          newsId: payload?.clickedItem?.id ?? null,
          requestId: Date.now(),
        });
      }

      rememberMostVisitedScroll();

      setIsSavedMode(false);
      setMostVisitedFilter("24h");
      setIsMostVisitedMode(true);
      setIsMostVisitedPageAtTop(true);
      scrollAppToTop();
    },
    [
      rememberMostVisitedScroll,
      scrollAppToTop,
      setIsMostVisitedMode,
      setIsMostVisitedPageAtTop,
      setIsSavedMode,
      setMostVisitedFilter,
      setMostVisitedTarget,
    ]
  );

  const closeMostVisitedMode = useCallback(() => {
    setIsMostVisitedMode(false);
    restoreMostVisitedScroll();
  }, [
    restoreMostVisitedScroll,
    setIsMostVisitedMode,
  ]);

  const openSavedMode = useCallback(() => {
    rememberSavedScroll();

    setFocusSource(null);
    setIsSourcePanelOpen(false);
    setIsMostVisitedMode(false);
    setSavedFilter("all");
    setIsSavedMode(true);
    setSavedScrollToTopSignal((prev) => prev + 1);
    setIsSavedPageAtTop(true);
    scrollAppToTop();
  }, [
    rememberSavedScroll,
    scrollAppToTop,
    setFocusSource,
    setIsMostVisitedMode,
    setIsSavedMode,
    setIsSourcePanelOpen,
    setIsSavedPageAtTop,
    setSavedFilter,
    setSavedScrollToTopSignal,
  ]);

  const closeSavedMode = useCallback(() => {
    setIsSavedMode(false);
    restoreSavedScroll();
  }, [
    restoreSavedScroll,
    setIsSavedMode,
  ]);

  /*
  |--------------------------------------------------------------------------
  | Keep Home and Focus display datasets isolated.
  |--------------------------------------------------------------------------
  |
  | Focus Mode reads only focusNewsBySource. It never reads directly from
  | Home state after the source has been seeded, and Focus pagination never
  | appends items to allNews.
  |
  */
  const homeNews = useMemo(() => {
    if (!activeSources || activeSources.length === 0) return [];

    return sortNewsByPublished(
      allNews.filter((item) => activeSources.includes(item.source))
    );
  }, [allNews, activeSources]);

  useEffect(() => {
    setInsertPollingContext({
      topCursor: getLatestNewsCursor(homeNews),
      deepestReadCursor: deepestHomeReadNews
        ? getLatestNewsCursor([deepestHomeReadNews])
        : { id: 0, sortDate: "" },
      sources: activeSources || [],
    });
  }, [
    activeSources,
    deepestHomeReadNews,
    homeNews,
    setInsertPollingContext,
  ]);

  const filteredNews = useMemo(() => {
    if (focusSource) {
      return getSourceMapValue(
        focusNewsBySource,
        focusSource,
        []
      );
    }

    return homeNews;
  }, [focusNewsBySource, focusSource, homeNews]);

  const olderNewsSources = useMemo(() => {
    if (!activeSources || activeSources.length === 0) return [];
    if (
      availableSources.length > 0 &&
      activeSources.length === availableSources.length
    ) {
      return [];
    }

    return activeSources;
  }, [activeSources, availableSources.length]);

  /*
  |-------------------------------------------------------------------------
  | Rebuild Home after a user changes the selected source set
  |-------------------------------------------------------------------------
  |
  | Mobile batches all toggles while Source Panel is open and refreshes once
  | when the user returns to Home. Desktop refreshes immediately because the
  | panel and feed are visible together. A source-filtered first page replaces
  | the old snapshot so Load More continues from the correct new cursor.
  |
  */
  useEffect(() => {
    if (
      sourceSelectionRevision <=
      lastRefreshedSourceSelectionRevisionRef.current
    ) {
      return undefined;
    }

    if (
      loading ||
      isBrowserOffline ||
      activeSources === null ||
      activeSources.length === 0 ||
      availableSources.length === 0
    ) {
      return undefined;
    }

    if (isMobile && isSourcePanelOpen) {
      return undefined;
    }

    let disposed = false;
    const requestedRevision = sourceSelectionRevision;

    const refreshSelectedSourceSnapshot = async () => {
      const result = await replaceHomeFeedWithLatestSnapshot({
        sources: olderNewsSources,
      });

      if (disposed || result.superseded || !result.success) {
        return;
      }

      lastRefreshedSourceSelectionRevisionRef.current = Math.max(
        lastRefreshedSourceSelectionRevisionRef.current,
        requestedRevision
      );

      setDeepestHomeReadNews(null);
      setMainListTarget(null);
      setFocusSource(null);
      scrollToTop();
    };

    refreshSelectedSourceSnapshot();

    return () => {
      disposed = true;
    };
  }, [
    activeSources,
    availableSources.length,
    isBrowserOffline,
    isMobile,
    isSourcePanelOpen,
    loading,
    olderNewsSources,
    replaceHomeFeedWithLatestSnapshot,
    scrollToTop,
    setFocusSource,
    sourceSelectionRevision,
  ]);

  useEffect(() => {
    if (loadMoreTimeoutRef.current) {
      window.clearTimeout(loadMoreTimeoutRef.current);
      loadMoreTimeoutRef.current = null;
    }

    setIsLoadingMore(false);
  }, [activeSources, focusSource, isMostVisitedMode, isSavedMode]);

  const homeHasMore =
    hasMoreHomeNewsForSources(olderNewsSources);

  const hasMore =
    !isMostVisitedMode &&
    !isSavedMode &&
    !focusSource &&
    homeHasMore;

  const hasNoSelectedSources =
    activeSources !== null &&
    activeSources.length === 0 &&
    availableSources.length > 0;

  const hasLoadedHomeContent = allNews.length > 0;

  /*
  |--------------------------------------------------------------------------
  | Initial error presentation
  |--------------------------------------------------------------------------
  |
  | An initial request failure must not replace the whole application with a
  | centered red message. When no news has been loaded yet, the normal app
  | skeleton remains visible and the error is surfaced only through the top
  | ticker. If older content already exists, that content remains usable.
  |
  */
  const showInitialErrorSkeleton =
    !loading && Boolean(error) && !hasLoadedHomeContent;

  const showBlockingFeedSkeleton =
    loading || showInitialErrorSkeleton;

  const showNoSourcesTicker =
    !showBlockingFeedSkeleton && !error && hasNoSelectedSources;

  const showSourcePanelOnly = isMobile && hasNoSelectedSources;

  /*
  |--------------------------------------------------------------------------
  | Home initial display scope
  |--------------------------------------------------------------------------
  |
  | Filtering Home to a smaller source set can leave only one or two already
  | loaded cards. The virtual list then becomes visible and starts fetching
  | older pages underneath the user. Keep that source scope behind the normal
  | feed skeleton until one complete visible batch is available.
  |
  | This gate is only for the initial visible batch of a scope. Once a scope
  | has been revealed, normal Load More never brings the full skeleton back.
  |
  */
  const homeFeedScopeKey = useMemo(() => {
    const sourceKeys = sortSourceListAlphabetically(activeSources || [])
      .map(getSourceKey)
      .filter(Boolean);

    return `home:${sourceKeys.join("|") || "none"}`;
  }, [activeSources]);

  useEffect(() => {
    setDeepestHomeReadNews(null);
  }, [homeFeedScopeKey]);

  const isHomeFeedMode =
    focusSource === null &&
    !isMostVisitedMode &&
    !isSavedMode &&
    !hasNoSelectedSources;

  const homeInitialBatchSize =
    NEWS_CONFIG.homeInitialVisibleBatchSize;

  const isHomeInitialDataReady =
    !homeSnapshotRefreshing &&
    (
      homeNews.length >= homeInitialBatchSize ||
      !homeHasMore
    );

  useEffect(() => {
    if (
      !isHomeFeedMode ||
      showBlockingFeedSkeleton ||
      homeSnapshotRefreshing ||
      isHomeInitialDataReady
    ) {
      setIsPreparingHomeInitialBatch(false);
      return undefined;
    }

    let retryTimerId = null;

    const scheduleAnotherCheck = () => {
      retryTimerId = window.setTimeout(() => {
        setHomePreparationCycle((previous) => previous + 1);
      }, 750);
    };

    const prepareHomeInitialBatch = async () => {
      if (homePreparationInFlightRef.current) {
        scheduleAnotherCheck();
        return;
      }

      if (!canAttemptHomeLoadMoreForSources(olderNewsSources)) {
        scheduleAnotherCheck();
        return;
      }

      homePreparationInFlightRef.current = true;
      setIsPreparingHomeInitialBatch(true);

      try {
        await fetchOlderNews(homeNews, olderNewsSources);
      } finally {
        homePreparationInFlightRef.current = false;
        setIsPreparingHomeInitialBatch(false);
        setHomePreparationCycle((previous) => previous + 1);
      }
    };

    prepareHomeInitialBatch();

    return () => {
      if (retryTimerId !== null) {
        window.clearTimeout(retryTimerId);
      }
    };
  }, [
    canAttemptHomeLoadMoreForSources,
    fetchOlderNews,
    homeNews,
    homePreparationCycle,
    homeSnapshotRefreshing,
    isHomeFeedMode,
    isHomeInitialDataReady,
    olderNewsSources,
    showBlockingFeedSkeleton,
  ]);

  useEffect(() => {
    if (!hasNoSelectedSources) return;

    setFocusSource(null);
    setIsMostVisitedMode(false);
    setIsSavedMode(false);

    if (isMobile) {
      setIsSourcePanelOpen(true);
    }
  }, [
    hasNoSelectedSources,
    isMobile,
    setFocusSource,
    setIsMostVisitedMode,
    setIsSavedMode,
    setIsSourcePanelOpen,
  ]);

  const loadMore = useCallback(() => {
    if (
      !hasMore ||
      isLoadingMore ||
      loadMoreTimeoutRef.current ||
      !canAttemptHomeLoadMoreForSources(olderNewsSources)
    ) {
      return;
    }

    setIsLoadingMore(true);

    loadMoreTimeoutRef.current = window.setTimeout(async () => {
      try {
        await fetchOlderNews(
          homeNews,
          olderNewsSources
        );
      } finally {
        setIsLoadingMore(false);
        loadMoreTimeoutRef.current = null;
      }
    }, NEWS_CONFIG.loadMoreSkeletonDelayMs);
  }, [
    canAttemptHomeLoadMoreForSources,
    fetchOlderNews,
    homeNews,
    hasMore,
    isLoadingMore,
    olderNewsSources,
  ]);

  const loadMoreFocusSource = useCallback(
    (source, sourceNews) => {
      if (
        !source ||
        getSourceMapValue(loadingFocusSources, source, false) ||
        loadMoreTimeoutRef.current
      ) {
        return;
      }

      if (
        getSourceMapValue(hasMoreNewsBySource, source, true) === false
      ) {
        return;
      }

      loadMoreTimeoutRef.current = window.setTimeout(async () => {
        try {
          await fetchFocusSourceNews(
            source,
            sourceNews,
            NEWS_CONFIG.loadMoreBatchSize
          );
        } finally {
          loadMoreTimeoutRef.current = null;
        }
      }, NEWS_CONFIG.loadMoreSkeletonDelayMs);
    },
    [fetchFocusSourceNews, hasMoreNewsBySource, loadingFocusSources]
  );

  const ensureFocusSourceInitial = useCallback(
    async (source, sourceNews = []) => {
      if (
        !source ||
        getSourceMapValue(loadingFocusSources, source, false) ||
        getSourceMapValue(failedFocusSources, source, false)
      ) {
        return;
      }

      if (
        getSourceMapValue(hasMoreNewsBySource, source, true) === false
      ) {
        return;
      }

      /*
      |--------------------------------------------------------------------------
      | Copy Home items into the isolated Focus store.
      |--------------------------------------------------------------------------
      |
      | This is a one-way copy. Future Focus batches only update
      | focusNewsBySource and can never change Home ordering.
      |
      */
      const homeSeedItems = allNews.filter((item) => {
        return isSameSource(item.source, source);
      });

      const isolatedSourceNews = mergeUniqueNewsByPublished(
        sourceNews,
        homeSeedItems
      );

      seedFocusSourceNews(source, homeSeedItems);

      if (
        isolatedSourceNews.length >=
          NEWS_CONFIG.focusSourceInitialBatchSize ||
        getSourceMapValue(checkedFocusSources, source, false)
      ) {
        return;
      }

      await fetchFocusSourceNews(
        source,
        isolatedSourceNews,
        NEWS_CONFIG.focusSourceInitialBatchSize,
        { useCursor: false }
      );
    },
    [
      allNews,
      checkedFocusSources,
      failedFocusSources,
      fetchFocusSourceNews,
      hasMoreNewsBySource,
      loadingFocusSources,
      seedFocusSourceNews,
    ]
  );

  const retryFocusSourceInitial = useCallback(
    async (source, sourceNews = []) => {
      if (!source) return 0;

      return retryFocusSourceNews(
        source,
        sourceNews,
        NEWS_CONFIG.focusSourceInitialBatchSize
      );
    },
    [retryFocusSourceNews]
  );

  const ensureAndScroll = useCallback(
    (newsOrLink) => {
      const targetNews =
        typeof newsOrLink === "string"
          ? filteredNews.find((item) => item.link === newsOrLink)
          : newsOrLink;

      const link =
        typeof newsOrLink === "string"
          ? newsOrLink
          : newsOrLink?.link;

      if (!targetNews && !link) return;

      const target = {
        id: targetNews?.id ?? null,
        link: targetNews?.link || link,
        requestId: Date.now(),
      };

      if (isMostVisitedMode) {
        closeMostVisitedMode();
        window.setTimeout(() => setMainListTarget(target), 100);
        return;
      }

      if (isSavedMode) {
        closeSavedMode();
        window.setTimeout(() => setMainListTarget(target), 100);
        return;
      }

      setMainListTarget(target);
    },
    [
      closeMostVisitedMode,
      closeSavedMode,
      filteredNews,
      isMostVisitedMode,
      isSavedMode,
    ]
  );

  const quickAccessSources = useQuickAccessSources({
    allNews,
    availableSources,
    activeSources,
    unreadCounts,
    focusSource,
  });

  const pendingHomeState = useMemo(() => {
    const activeSourceSet = new Set(activeSources || []);
    const relevantNews = pendingNews.filter((item) => {
      return activeSourceSet.has(item?.source);
    });
    const newestHomeNews = homeNews[0] || null;
    const freshNews = [];
    const readBackfillNews = [];

    relevantNews.forEach((item) => {
      const isFresh =
        !newestHomeNews ||
        compareNewsByPublished(item, newestHomeNews) < 0;

      if (isFresh) {
        freshNews.push(item);
        return;
      }

      if (
        deepestHomeReadNews &&
        compareNewsByPublished(item, deepestHomeReadNews) <= 0
      ) {
        readBackfillNews.push(item);
      }
    });

    return {
      relevantNews,
      freshNews,
      readBackfillNews,
    };
  }, [
    activeSources,
    deepestHomeReadNews,
    homeNews,
    pendingNews,
  ]);

  useEffect(() => {
    if (!largeBacklog?.sessionKey) return;

    registerSuppressedBackfillCounts(
      largeBacklog.sessionKey,
      largeBacklog.readBackfillCountsBySource || {}
    );

    /*
    | A backlog containing no genuinely newer Home news stays completely
    | invisible by product policy. Its cursor has already advanced and its
    | already-read backfills are persisted above, so no hidden capsule/session
    | should remain waiting for a click that can never happen.
    */
    if ((Number(largeBacklog.freshCount) || 0) === 0) {
      clearLargeBacklog();
    }
  }, [
    clearLargeBacklog,
    largeBacklog,
    registerSuppressedBackfillCounts,
  ]);

  const autoReadPendingKeysRef = useRef(new Set());

  useEffect(() => {
    const pendingKeys = new Set(
      pendingNews.map(getNewsKey).filter(Boolean)
    );

    autoReadPendingKeysRef.current.forEach((key) => {
      if (!pendingKeys.has(key)) {
        autoReadPendingKeysRef.current.delete(key);
      }
    });

    const itemsToMark = pendingHomeState.readBackfillNews.filter((item) => {
      const key = getNewsKey(item);

      return key && !autoReadPendingKeysRef.current.has(key);
    });

    if (itemsToMark.length === 0) return;

    itemsToMark.forEach((item) => {
      const key = getNewsKey(item);
      if (key) autoReadPendingKeysRef.current.add(key);
    });

    markItemsAsRead(itemsToMark, {
      trackUnread: false,
    });
  }, [
    markItemsAsRead,
    pendingHomeState.readBackfillNews,
    pendingNews,
  ]);

  const handleShowHeldNews = useCallback(async () => {
    const relevantNews = pendingHomeState.relevantNews;

    /*
    |--------------------------------------------------------------------------
    | Large Backlog acceptance replaces the Home snapshot instead of merging.
    |--------------------------------------------------------------------------
    |
    | Keeping the old 100 rows below a new snapshot would create a multi-hour
    | hole in the middle of the feed. The old live list is therefore discarded,
    | a new first page is loaded, and Load More continues from that new page.
    |
    */
    if (largeBacklog) {
      registerSuppressedBackfillCounts(
        largeBacklog.sessionKey,
        largeBacklog.readBackfillCountsBySource || {}
      );

      if (relevantNews.length > 0) {
        markItemsAsRead(
          pendingHomeState.readBackfillNews,
          {
            trackUnread: false,
          }
        );

        registerCommittedUnreadItems(
          relevantNews,
          pendingHomeState.readBackfillNews
        );

        commitPendingNewsToUnread(relevantNews);
      }

      commitPendingCountsToUnread(
        largeBacklog.freshCountsBySource || {}
      );

      const result = await replaceHomeFeedWithLatestSnapshot({
        sources: olderNewsSources,
      });

      if (!result.success) return;

      setDeepestHomeReadNews(null);
      setFocusSource(null);
      scrollToTop();
      return;
    }

    if (relevantNews.length === 0) return;

    const linksToHighlight = relevantNews.map((item) => item.link);
    const relevantKeys = new Set(relevantNews.map(getNewsKey).filter(Boolean));
    const nextAllNews = mergeUniqueNewsByPublished(relevantNews, allNews);

    markItemsAsRead(
      pendingHomeState.readBackfillNews,
      {
        trackUnread: false,
      }
    );
    registerCommittedUnreadItems(
      relevantNews,
      pendingHomeState.readBackfillNews
    );
    commitPendingNewsToUnread(relevantNews);
    setAllNews(nextAllNews);

    setPendingNews((prev) =>
      prev.filter((item) => {
        const key = getNewsKey(item);
        return !key || !relevantKeys.has(key);
      })
    );

    setFocusSource(null);

    setTimeout(() => {
      linksToHighlight.forEach((link) => {
        const element = document.getElementById(
          `news-${encodeURIComponent(link)}`
        );

        if (element) {
          element.classList.add("news-card--highlight");

          setTimeout(() => {
            element.classList.remove("news-card--highlight");
          }, 2000);
        }
      });

      scrollToTop();
    }, 100);
  }, [
    allNews,
    commitPendingCountsToUnread,
    commitPendingNewsToUnread,
    largeBacklog,
    markItemsAsRead,
    olderNewsSources,
    pendingHomeState,
    registerCommittedUnreadItems,
    registerSuppressedBackfillCounts,
    replaceHomeFeedWithLatestSnapshot,
    scrollToTop,
    setAllNews,
    setFocusSource,
    setPendingNews,
  ]);


  const restorePersistentHomeScroll = useCallback(() => {
    const scrollTarget = isMobile
      ? contentScrollParent
      : mainScrollParent;

    if (!scrollTarget) return;

    window.requestAnimationFrame(() => {
      scrollTarget.scrollTo({
        top: homeScrollTopRef.current,
        behavior: "auto",
      });
    });
  }, [
    contentScrollParent,
    isMobile,
    mainScrollParent,
  ]);

  const handleSourceFocus = useCallback(
    (source) => {
      const willOpenSource = focusSource !== source;
      const homeScrollTarget = isMobile
        ? contentScrollParent
        : mainScrollParent;

      setIsSavedMode(false);

      if (!willOpenSource) {
        setFocusSource(null);
        setIsFocusPageAtTop(true);
        restorePersistentHomeScroll();
        return;
      }

      if (homeScrollTarget) {
        homeScrollTopRef.current = homeScrollTarget.scrollTop || 0;
      }

      if (getSourceMapValue(failedFocusSources, source, false)) {
        retryFocusSourceInitial(
          source,
          getSourceMapValue(focusNewsBySource, source, [])
        );
      }

      setFocusSource(source);
      setIsFocusPageAtTop(true);

      if (homeScrollTarget) {
        window.requestAnimationFrame(() => {
          homeScrollTarget.scrollTo({
            top: 0,
            behavior: "auto",
          });
        });
      }
    },
    [
      contentScrollParent,
      failedFocusSources,
      focusNewsBySource,
      focusSource,
      isMobile,
      mainScrollParent,
      restorePersistentHomeScroll,
      retryFocusSourceInitial,
      setFocusSource,
      setIsFocusPageAtTop,
      setIsSavedMode,
    ]
  );

  const handleExitFocus = useCallback(() => {
    setFocusSource(null);
    restorePersistentHomeScroll();
  }, [
    restorePersistentHomeScroll,
    setFocusSource,
  ]);

  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key !== "Escape") return;

      if (sharedNewsId) {
        event.preventDefault();
        closeSharedNews();
        return;
      }

      if (isMostVisitedMode) {
        event.preventDefault();
        closeMostVisitedMode();
        return;
      }

      if (isSavedMode) {
        event.preventDefault();
        closeSavedMode();
        return;
      }

      if (focusSource !== null) {
        event.preventDefault();
        handleExitFocus();
      }
    };

    window.addEventListener("keydown", handleEscapeKey);

    return () => window.removeEventListener("keydown", handleEscapeKey);
  }, [
    closeMostVisitedMode,
    closeSavedMode,
    closeSharedNews,
    focusSource,
    handleExitFocus,
    isMostVisitedMode,
    isSavedMode,
    sharedNewsId,
  ]);

  useEffect(() => {
    if (!isSavedMode || savedNews.length > 0) return;

    setSavedFilter("all");
    closeSavedMode();
  }, [
    closeSavedMode,
    isSavedMode,
    savedNews.length,
    setSavedFilter,
  ]);


  const displayedNews = filteredNews;

  const adDevice = isMobile ? "mobile" : "desktop";

  const homeAdPlan = useAdPlan({
    enabled: !hasNoSelectedSources,
    surface: "home",
    device: adDevice,
    feedKey: `home:${homeFeedScopeKey}`,
    newsCount: homeNews.length,
  });

  const desktopFocusAdPlan = useAdPlan({
    enabled: !isMobile && focusSource !== null,
    surface: "focus",
    device: "desktop",
    feedKey: `focus:${getSourceKey(focusSource) || "none"}`,
    newsCount: displayedNews.length,
  });

  /*
  |--------------------------------------------------------------------------
  | Persistent Home feed
  |--------------------------------------------------------------------------
  |
  | Home stays mounted while Focus Mode is open. Its data, Virtuoso viewport
  | and visual-readiness state therefore remain intact. Returning from Focus
  | only reveals the already-mounted Home layer; it never starts a new initial
  | skeleton cycle.
  |
  */
  const homeFeedScrollParent =
    (isMobile ? contentScrollParent : mainScrollParent) || undefined;

  const {
    isVisualReady: isHomeFeedVisualReady,
    handleRangeChanged: handleHomeFeedRangeChanged,
  } = useInitialFeedVisualReady({
    feedKey: homeFeedScopeKey,
    items: homeNews,
    enabled: !hasNoSelectedSources,
    isInitialDataReady: isHomeInitialDataReady,
    scrollParent: homeFeedScrollParent,
  });

  const showHomeFeedSkeleton =
    !isHomeInitialDataReady ||
    !isHomeFeedVisualReady;

  /*
  |--------------------------------------------------------------------------
  | One atomic startup skeleton
  |--------------------------------------------------------------------------
  |
  | The startup AppSkeleton begins only after the Launch Screen is removed and
  | remains visible for one controlled second. This keeps the startup sequence
  | deterministic: one second of logo/version followed by one second of the
  | full Home skeleton. After that window, normal Home loading states take over.
  |
  | This gate is completed only once per application mount. Later source
  | filter changes continue to use the normal Home feed skeleton.
  |
  */
  useEffect(() => {
    if (hasInitialSkeletonMinimumElapsed) return undefined;

    let minimumTimerId = null;

    const startMinimumSkeletonWindow = () => {
      if (minimumTimerId !== null) return;

      minimumTimerId = window.setTimeout(() => {
        setHasInitialSkeletonMinimumElapsed(true);
      }, 1000);
    };

    if (window.__CHEKHABARE_LAUNCH_SCREEN_HIDDEN__) {
      startMinimumSkeletonWindow();
    } else {
      window.addEventListener(
        "chekhabare:launch-screen-hidden",
        startMinimumSkeletonWindow,
        { once: true }
      );
    }

    return () => {
      window.removeEventListener(
        "chekhabare:launch-screen-hidden",
        startMinimumSkeletonWindow
      );

      if (minimumTimerId !== null) {
        window.clearTimeout(minimumTimerId);
      }
    };
  }, [hasInitialSkeletonMinimumElapsed]);

  useEffect(() => {
    if (hasCompletedInitialAppReveal) return;
    if (!hasInitialSkeletonMinimumElapsed) return;

    /*
    |-------------------------------------------------------------------------
    | Keep one startup skeleton until Home is actually ready
    |-------------------------------------------------------------------------
    |
    | Previously the startup AppSkeleton was forcibly removed after its
    | one-second minimum even when the first Home request had no usable data.
    | That exposed the separate FocusPagerSkeleton underneath, so offline, VPN
    | failure, timeout, and backend-outage launches visibly switched to a
    | completely different skeleton.
    |
    | On the Home surface, keep the startup skeleton mounted until the real
    | Home feed has passed its visual-readiness gate (or the user has no
    | selected sources). Other explicit surfaces keep the existing one-second
    | startup handoff so deep links / Saved / Most Visited / Focus are not
    | blocked by Home readiness. If connectivity recovers, the normal retry
    | path makes Home ready and this overlay is released automatically.
    |
    */
    const canRevealHome =
      !isHomeFeedMode ||
      hasNoSelectedSources ||
      isHomeFeedVisualReady;

    if (!canRevealHome) return;

    setHasCompletedInitialAppReveal(true);
  }, [
    hasCompletedInitialAppReveal,
    hasInitialSkeletonMinimumElapsed,
    hasNoSelectedSources,
    isHomeFeedMode,
    isHomeFeedVisualReady,
  ]);

  const showInitialAppSkeleton =
    !hasCompletedInitialAppReveal;

  const popupSurface =
    !showInitialAppSkeleton &&
    !isMostVisitedMode &&
    !isSavedMode &&
    !sharedNewsId &&
    !hasNoSelectedSources
      ? focusSource !== null
        ? "focus"
        : "home"
      : null;

  const popupNotice = usePopupNotice({
    enabled: Boolean(popupSurface),
    surface: popupSurface,
    device: adDevice,
  });

  /*
  |--------------------------------------------------------------------------
  | Desktop Focus visual gate
  |--------------------------------------------------------------------------
  |
  | Mobile Focus owns its own source-bound readiness inside FocusPagerPage.
  | Desktop Focus remains mounted behind source-scoped Most Visited, just as
  | Home stays mounted behind other modes. Its visual-ready state and Virtuoso
  | viewport therefore survive the round trip without a new skeleton cycle.
  |
  */
  const hasDesktopFocusFeed =
    !isMobile &&
    focusSource !== null &&
    !isSavedMode;

  const isDesktopFocusFeedMode =
    hasDesktopFocusFeed &&
    !isMostVisitedMode;

  const desktopFocusFeedKey =
    `desktop-focus:${getSourceKey(focusSource)}`;

  const desktopFocusInitialDataReady =
    !hasDesktopFocusFeed ||
    displayedNews.length >= NEWS_CONFIG.focusSourceInitialBatchSize ||
    Boolean(
      getSourceMapValue(
        checkedFocusSources,
        focusSource,
        false
      )
    ) ||
    getSourceMapValue(
      hasMoreNewsBySource,
      focusSource,
      true
    ) === false;

  const {
    isVisualReady: isDesktopFocusVisualReady,
    handleRangeChanged: handleDesktopFocusRangeChanged,
  } = useInitialFeedVisualReady({
    feedKey: desktopFocusFeedKey,
    items: displayedNews,
    enabled:
      hasDesktopFocusFeed &&
      !showBlockingFeedSkeleton,
    isInitialDataReady: desktopFocusInitialDataReady,
    scrollParent: mainScrollParent || undefined,
  });

  const showDesktopFocusSkeleton =
    isDesktopFocusFeedMode &&
    (
      !desktopFocusInitialDataReady ||
      !isDesktopFocusVisualReady
    );

  const desktopFocusHasMore = getSourceMapValue(
    hasMoreNewsBySource,
    focusSource,
    true
  ) !== false;

  const desktopFocusIsLoadingMore = Boolean(
    getSourceMapValue(
      loadingFocusSources,
      focusSource,
      false
    )
  );

  useEffect(() => {
    if (!focusSource || isMobile || isMostVisitedMode || isSavedMode) return;

    ensureFocusSourceInitial(focusSource, displayedNews);
  }, [
    displayedNews,
    ensureFocusSourceInitial,
    focusSource,
    isMobile,
    isMostVisitedMode,
    isSavedMode,
  ]);

  const showFilteredEmptyStatus = filteredNews.length === 0;
  const showHomeEmptyStatus = homeNews.length === 0;

  const handlePagerSourceChange = useCallback((source) => {
    if (getSourceMapValue(failedFocusSources, source, false)) {
      retryFocusSourceInitial(
        source,
        getSourceMapValue(focusNewsBySource, source, [])
      );
    }

    setIsFocusPageAtTop(true);
    setFocusSource(source);
  }, [
    failedFocusSources,
    focusNewsBySource,
    retryFocusSourceInitial,
    setFocusSource,
    setIsFocusPageAtTop,
  ]);

  const handleMostVisitedFilterChange = useCallback(
    (filter) => {
      const isChangingFilter = filter !== mostVisitedFilter;
      const didChange = updateMostVisitedFilter(filter);

      if (!didChange) return;

      setIsMostVisitedPageAtTop(true);

      /*
      |---------------------------------------------------------------------
      | Desktop Most Visited period reset
      |---------------------------------------------------------------------
      |
      | Desktop reuses the main application scroller for all three periods.
      | Without resetting that shared scroller, switching from a short or
      | fully-scrolled 24-hour feed opens Week / Month at the same old offset.
      |
      | Mobile owns one vertical scroller per pager page, so its existing
      | per-page behavior must remain untouched.
      |
      */
      if (!isMobile && isChangingFilter) {
        scrollAppToTop();
      }
    },
    [
      isMobile,
      mostVisitedFilter,
      scrollAppToTop,
      setIsMostVisitedPageAtTop,
      updateMostVisitedFilter,
    ]
  );

  const handleSavedFilterChange = useCallback((filter) => {
    setIsSavedPageAtTop(true);
    setSavedFilter(filter);
  }, [
    setIsSavedPageAtTop,
    setSavedFilter,
  ]);

  const handleSavedTickerClick = useCallback(
    (payload) => {
      const link = payload?.clickedItem?.link;
      if (!link) return;

      scrollToNewsItem(link);
    },
    [scrollToNewsItem]
  );

  const pendingFreshPresentation = useMemo(() => {
    const freshPending = pendingHomeState.freshNews;
    const largeFreshCount = Math.max(
      0,
      Number(largeBacklog?.freshCount) || 0
    );
    const sourceSet = new Set(
      freshPending
        .map((item) => item?.source)
        .filter(Boolean)
    );

    Object.entries(
      largeBacklog?.freshCountsBySource || {}
    ).forEach(([source, count]) => {
      if ((Number(count) || 0) > 0) {
        sourceSet.add(source);
      }
    });

    return {
      count: freshPending.length + largeFreshCount,
      sources: Array.from(sourceSet),
    };
  }, [largeBacklog, pendingHomeState.freshNews]);

  const pendingCapsule = useMemo(() => {
    const pendingFreshCount = pendingFreshPresentation.count;
    const isPendingFreshCountCapped = pendingFreshCount > 99;
    const pendingFreshCountText = isPendingFreshCountCapped
      ? `+${toPersianDigits(99)}`
      : toPersianDigits(pendingFreshCount);

    if (
      !isMobile ||
      focusSource !== null ||
      isMostVisitedMode ||
      isSavedMode ||
      pendingFreshCount === 0
    ) {
      return null;
    }

    const sources = pendingFreshPresentation.sources;
    const visibleSources = sources.slice(0, 3);
    const moreCount = sources.length - visibleSources.length;

    return (
      <button
        type="button"
        className="pending-capsule"
        onClick={handleShowHeldNews}
        aria-label={`${pendingFreshCountText} خبر تازه`}
      >
        <span className="pending-capsule__icons">
          {visibleSources.map((source, index) => (
            <span
              key={source}
              className="pending-capsule__icon-wrap"
              style={{ zIndex: index + 1 }}
              title={toPersianDigits(source)}
            >
              {favicons[source] ? (
                <RetryLogoImage
                  src={favicons[source]}
                  alt=""
                  className="pending-capsule__icon"
                  fallbackText={source.charAt(0)}
                  loading="lazy"
                  decoding="async"
                />
              ) : (
                <span className="pending-capsule__default-icon">
                  {source.charAt(0)}
                </span>
              )}
            </span>
          ))}

          {moreCount > 0 && (
            <span
              className="pending-capsule__more"
              title={`${toPersianDigits(moreCount)} منبع دیگر`}
            >
              {toPersianDigits(moreCount)}+
            </span>
          )}
        </span>

        <span className="pending-capsule__text">
          {isPendingFreshCountCapped ? (
            <span dir="ltr">{pendingFreshCountText}</span>
          ) : (
            pendingFreshCountText
          )}{" "}
          خبر تازه
        </span>
      </button>
    );
  }, [
    pendingFreshPresentation,
    isMobile,
    focusSource,
    isMostVisitedMode,
    isSavedMode,
    favicons,
    handleShowHeldNews,
  ]);

  useEffect(() => {
    if (!isMostVisitedMode || isMobile || !mostVisitedTarget?.link) return;

    const targetFilter = MOST_VISITED_FILTERS.find((filter) => {
      return (mostVisitedPages[filter] || []).some(
        (item) => item.link === mostVisitedTarget.link
      );
    });

    if (!targetFilter) {
      if (hasLoadedAllMostVisited && !isMostVisitedLoading) {
        setMostVisitedTarget(null);
      }

      return;
    }

    if (targetFilter !== mostVisitedFilter) {
      setMostVisitedFilter(targetFilter);
      setIsMostVisitedPageAtTop(true);
      return;
    }

    const timeoutId = window.setTimeout(() => {
      /*
      |-----------------------------------------------------------------------
      | Scope ticker highlighting to the visible desktop Most Visited feed.
      |-----------------------------------------------------------------------
      |
      | Home and desktop Focus stay mounted behind Most Visited so their scroll
      | positions remain frozen. The same news can therefore exist in more than
      | one mounted layer with the same DOM id. A document-level lookup may hit
      | a hidden card and apply the highlight there. Searching only inside the
      | active Most Visited layer keeps scrolling and highlighting on the card
      | the user can actually see.
      |
      */
      const activeMostVisitedFeed = document.querySelector(
        ".news-grid--most-visited .persistent-mode-feed"
      );

      if (!activeMostVisitedFeed) return;

      const targetLinkId = `news-${encodeURIComponent(
        mostVisitedTarget.link
      )}`;
      const targetNewsId = mostVisitedTarget.newsId;

      const element = Array.from(
        activeMostVisitedFeed.querySelectorAll(".news-card")
      ).find((card) => {
        const hasMatchingLink = card.id === targetLinkId;
        const hasMatchingNewsId =
          targetNewsId !== undefined &&
          targetNewsId !== null &&
          String(card.dataset.newsId || "") === String(targetNewsId);

        return hasMatchingLink || hasMatchingNewsId;
      });

      if (!element) return;

      element.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });

      element.classList.add("news-card--highlight");

      window.setTimeout(() => {
        element.classList.remove("news-card--highlight");
      }, 2000);

      setIsMostVisitedPageAtTop(false);
      setMostVisitedTarget(null);
    }, 80);

    return () => window.clearTimeout(timeoutId);
  }, [
    isMobile,
    isMostVisitedLoading,
    isMostVisitedMode,
    mostVisitedFilter,
    mostVisitedPages,
    hasLoadedAllMostVisited,
    mostVisitedTarget,
    setIsMostVisitedPageAtTop,
    setMostVisitedFilter,
    setMostVisitedTarget,
  ]);

  useEffect(() => {
    if (!isMostVisitedMode || !isMobile || !mostVisitedTarget?.link) return;

    const targetFilter = MOST_VISITED_FILTERS.find((filter) => {
      return (mostVisitedPages[filter] || []).some(
        (item) => item.link === mostVisitedTarget.link
      );
    });

    if (targetFilter && targetFilter !== mostVisitedFilter) {
      setMostVisitedFilter(targetFilter);
      setIsMostVisitedPageAtTop(true);
    }
  }, [
    isMobile,
    isMostVisitedMode,
    mostVisitedFilter,
    mostVisitedPages,
    mostVisitedTarget,
    setIsMostVisitedPageAtTop,
    setMostVisitedFilter,
  ]);

  const homeTickerNews = useMemo(() => {
    const items =
      !activeSources || activeSources.length === 0
        ? allNews
        : allNews.filter((item) => activeSources.includes(item.source));

    return sortNewsByPublished(items);
  }, [activeSources, allNews]);

  const tickerNews = isSavedMode
    ? savedNews
    : priorityMostVisitedTickerNews.length
    ? priorityMostVisitedTickerNews
    : homeTickerNews;

  const visibleTickerNews = tickerNews.length > 0 ? tickerNews : homeTickerNews;

  /*
  |--------------------------------------------------------------------------
  | Ticker datasets are mode-specific.
  |--------------------------------------------------------------------------
  |
  | Home ticker may use Most Visited or Home news.
  | Desktop Focus ticker uses the selected source's 24-hour Most Visited data.
  | Mobile Focus keeps its isolated Focus dataset for the source filter bar.
  | No ticker path writes to Home or Focus feed state.
  |
  */
  const focusFeedTickerFallbackNews = useMemo(() => {
    if (focusSource === null) return [];

    return sortNewsByPublished(
      getSourceMapValue(
        focusNewsBySource,
        focusSource,
        []
      )
    );
  }, [focusNewsBySource, focusSource]);

  const hasResolvedDesktopFocusMostVisited24h = Boolean(
    hasLoadedMostVisitedFilter("24h") ||
      mostVisitedErrorsByFilter["24h"]
  );

  const focusTickerNews = useMemo(() => {
    if (focusSource === null) return [];

    /*
    | Desktop Focus ticker prefers source-scoped Most Visited (24h).
    | A valid 24h response can still be empty for a quiet source. Once that
    | request settles, the already-loaded Focus feed becomes the safe fallback
    | so the ticker never reveals as an empty black bar.
    |
    | Mobile keeps using its isolated Focus feed because it renders the source
    | filter bar instead of the desktop scrolling news ticker.
    */
    if (!isMobile) {
      const sourceMostVisited24h = mostVisitedPages["24h"] || [];

      if (sourceMostVisited24h.length > 0) {
        return sourceMostVisited24h;
      }

      if (hasResolvedDesktopFocusMostVisited24h) {
        return focusFeedTickerFallbackNews;
      }

      return [];
    }

    return focusFeedTickerFallbackNews;
  }, [
    focusFeedTickerFallbackNews,
    focusSource,
    hasResolvedDesktopFocusMostVisited24h,
    isMobile,
    mostVisitedPages,
  ]);

  const showDesktopFocusTickerSkeleton = Boolean(
    showDesktopFocusSkeleton ||
      (
        !isMobile &&
        focusSource !== null &&
        !hasResolvedDesktopFocusMostVisited24h &&
        (
          mostVisitedLoadingByFilter["24h"] ||
          !hasLoadedMostVisitedFilter("24h")
        )
      )
  );

  /*
  |--------------------------------------------------------------------------
  | User-visible errors
  |--------------------------------------------------------------------------
  |
  | Request errors still remain available to the data hooks for retry and
  | recovery logic. The interface, however, only exposes the explicit offline
  | message. Server, timeout, parse and rate-limit errors stay internal.
  |
  */
  const visibleMostVisitedError =
    isMostVisitedMode &&
    (isBrowserOffline || mostVisitedError === OFFLINE_ERROR_MESSAGE)
      ? OFFLINE_ERROR_MESSAGE
      : "";

  const showMostVisitedStatus =
    isMostVisitedMode &&
    activeMostVisitedItems.length === 0 &&
    (mostVisitedError ||
      isActiveMostVisitedLoading ||
      !hasLoadedActiveMostVisited ||
      !(isMobile && mostVisitedTarget?.link));

  const hasVisibleOfflineError =
    isBrowserOffline ||
    error === OFFLINE_ERROR_MESSAGE ||
    refreshError === OFFLINE_ERROR_MESSAGE ||
    visibleMostVisitedError === OFFLINE_ERROR_MESSAGE;

  const showSavedStatus = isSavedMode && activeSavedItems.length === 0;
  const tickerErrorText = hasVisibleOfflineError
    ? OFFLINE_ERROR_MESSAGE
    : "";

  const showConnectionTicker = !loading && Boolean(tickerErrorText);
  const connectionTickerText = tickerErrorText;

  const isHomeLayerActive =
    focusSource === null &&
    !isMostVisitedMode &&
    !isSavedMode;

  const homeFeedView = (
    <div
      className={`persistent-home-feed${
        isHomeLayerActive
          ? " persistent-home-feed--active"
          : " persistent-home-feed--hidden"
      }`}
      aria-hidden={!isHomeLayerActive}
    >
      <div
        className={`initial-feed-stage${
          isHomeFeedVisualReady
            ? " initial-feed-stage--ready"
            : " initial-feed-stage--preparing"
        }`}
        aria-busy={
          showHomeFeedSkeleton ||
          isPreparingHomeInitialBatch
        }
      >
        {isHomeInitialDataReady && (
          <div
            className={`initial-feed-content${
              isHomeFeedVisualReady
                ? ""
                : " initial-feed-content--preparing"
            }`}
            aria-hidden={!isHomeFeedVisualReady}
          >
            <VirtualizedNewsList
              key={`home-feed:${homeFeedScopeKey}`}
              items={homeNews}
              scrollTarget={
                isHomeLayerActive && isHomeFeedVisualReady
                  ? mainListTarget
                  : null
              }
              onScrollTargetHandled={
                isHomeLayerActive && isHomeFeedVisualReady
                  ? () => {
                      setMainListTarget(null);
                    }
                  : undefined
              }
              favicons={favicons}
              onMarkAsRead={
                isHomeLayerActive && isHomeFeedVisualReady
                  ? handleHomeNewsVisible
                  : undefined
              }
              onNewsOpen={recordNewsView}
              onLoadMore={
                isHomeLayerActive && isHomeFeedVisualReady
                  ? loadMore
                  : undefined
              }
              hasMore={
                isHomeLayerActive && isHomeFeedVisualReady
                  ? hasMore
                  : false
              }
              isLoadingMore={
                isHomeLayerActive && isHomeFeedVisualReady
                  ? isLoadingMore
                  : false
              }
              scrollParent={homeFeedScrollParent}
              savedLinks={savedLinks}
              onToggleSaved={toggleSavedNews}
              onShare={handleShareNews}
              onView={handleViewNews}
              footerSpacerHeight={isMobile ? 88 : 0}
              onRangeChanged={handleHomeFeedRangeChanged}
              eagerImages={!isHomeFeedVisualReady}
              headerContent={
                <FirstAdCard
                  ad={homeAdPlan.first}
                  houseAd={homeAdPlan.houseAd}
                  surface="home"
                  device={adDevice}
                  analyticsSlot="first"
                  viewabilityEnabled={
                    isHomeLayerActive && isHomeFeedVisualReady
                  }
                />
              }
              regularAdsEvery={homeAdPlan.regularEvery}
              regularAds={homeAdPlan.regular}
              houseAd={homeAdPlan.houseAd}
              adSurface="home"
              adDevice={adDevice}
              adViewabilityEnabled={
                isHomeLayerActive && isHomeFeedVisualReady
              }
            />

            {isHomeLayerActive &&
              isHomeFeedVisualReady &&
              showHomeEmptyStatus && (
                <div className="status-message">
                  خبری با این فیلترها یافت نشد
                </div>
              )}
          </div>
        )}

        {showHomeFeedSkeleton && (
          <div
            className={
              isHomeInitialDataReady
                ? "initial-feed-overlay"
                : "initial-feed-skeleton"
            }
          >
            <FocusPagerSkeleton />
          </div>
        )}
      </div>
    </div>
  );

  const mobileFocusFeedView =
    isMobile && focusSource !== null ? (
      <FocusPager
        sources={activeSources || []}
        focusNewsBySource={focusNewsBySource}
        favicons={favicons}
        onMarkAsRead={markAsRead}
        onNewsOpen={recordNewsView}
        onSourceChange={handlePagerSourceChange}
        initialSource={focusSource}
        scrollToTopSignal={focusScrollToTopSignal}
        onActivePageAtTopChange={setIsFocusPageAtTop}
        savedLinks={savedLinks}
        onToggleSaved={toggleSavedNews}
        onShare={handleShareNews}
        onView={handleViewNews}
        onLoadMore={loadMoreFocusSource}
        onEnsureInitial={ensureFocusSourceInitial}
        hasMoreForSource={(source) =>
          getSourceMapValue(
            hasMoreNewsBySource,
            source,
            true
          ) !== false
        }
        isLoadingMoreSource={(source) =>
          Boolean(
            getSourceMapValue(
              loadingFocusSources,
              source,
              false
            )
          )
        }
        hasCheckedInitialForSource={(source) =>
          Boolean(
            getSourceMapValue(
              checkedFocusSources,
              source,
              false
            )
          )
        }
        hasFailedInitialForSource={(source) =>
          Boolean(
            getSourceMapValue(
              failedFocusSources,
              source,
              false
            )
          )
        }
        onRetryInitial={(source, sourceNews) =>
          retryFocusSourceInitial(source, sourceNews)
        }
        initialBatchSize={NEWS_CONFIG.focusSourceInitialBatchSize}
        scrollParent={contentScrollParent || undefined}
      />
    ) : null;

  const desktopFocusFeedView =
    hasDesktopFocusFeed ? (
      <div
        className={`persistent-desktop-focus-feed${
          isDesktopFocusFeedMode
            ? " persistent-desktop-focus-feed--active"
            : " persistent-desktop-focus-feed--hidden"
        }`}
        aria-hidden={!isDesktopFocusFeedMode}
      >
        <div
          className={`initial-feed-stage${
            isDesktopFocusVisualReady
              ? " initial-feed-stage--ready"
              : " initial-feed-stage--preparing"
          }`}
          aria-busy={
            isDesktopFocusFeedMode &&
            showDesktopFocusSkeleton
          }
        >
          {desktopFocusInitialDataReady && (
            <div
              className={`initial-feed-content${
                isDesktopFocusVisualReady
                  ? ""
                  : " initial-feed-content--preparing"
              }`}
              aria-hidden={!isDesktopFocusVisualReady}
            >
              <VirtualizedNewsList
                key={`desktop-focus-feed:${desktopFocusFeedKey}`}
                items={displayedNews}
                favicons={favicons}
                onMarkAsRead={
                  isDesktopFocusFeedMode &&
                  isDesktopFocusVisualReady
                    ? markAsRead
                    : undefined
                }
                onNewsOpen={recordNewsView}
                onLoadMore={
                  isDesktopFocusFeedMode &&
                  isDesktopFocusVisualReady
                    ? () =>
                        loadMoreFocusSource(
                          focusSource,
                          displayedNews
                        )
                    : undefined
                }
                hasMore={
                  isDesktopFocusFeedMode &&
                  isDesktopFocusVisualReady
                    ? desktopFocusHasMore
                    : false
                }
                isLoadingMore={
                  isDesktopFocusFeedMode &&
                  isDesktopFocusVisualReady
                    ? desktopFocusIsLoadingMore
                    : false
                }
                scrollParent={mainScrollParent || undefined}
                savedLinks={savedLinks}
                onToggleSaved={toggleSavedNews}
                onShare={handleShareNews}
                onView={handleViewNews}
                onRangeChanged={handleDesktopFocusRangeChanged}
                eagerImages={!isDesktopFocusVisualReady}
                headerContent={
                  <FirstAdCard
                    ad={desktopFocusAdPlan.first}
                    houseAd={desktopFocusAdPlan.houseAd}
                    surface="focus"
                    device="desktop"
                    analyticsSlot="first"
                    viewabilityEnabled={
                      isDesktopFocusFeedMode && isDesktopFocusVisualReady
                    }
                  />
                }
                regularAdsEvery={desktopFocusAdPlan.regularEvery}
                regularAds={desktopFocusAdPlan.regular}
                houseAd={desktopFocusAdPlan.houseAd}
                adSurface="focus"
                adDevice="desktop"
                adViewabilityEnabled={
                  isDesktopFocusFeedMode && isDesktopFocusVisualReady
                }
                adAnalyticsSlotPrefix={`focus:${
                  focusSource || "unknown"
                }`}
              />

              {isDesktopFocusFeedMode &&
                isDesktopFocusVisualReady &&
                showFilteredEmptyStatus && (
                  <div className="status-message">
                    خبری با این فیلترها یافت نشد
                  </div>
                )}
            </div>
          )}

          {showDesktopFocusSkeleton && (
            <div
              className={
                desktopFocusInitialDataReady
                  ? "initial-feed-overlay"
                  : "initial-feed-skeleton"
              }
            >
              <FocusPagerSkeleton />
            </div>
          )}
        </div>
      </div>
    ) : null;

  const mostVisitedFeedView = showMostVisitedStatus &&
    (isActiveMostVisitedLoading || !hasLoadedActiveMostVisited) &&
    !visibleMostVisitedError ? (
      Array.from({ length: 6 }, (_, index) => (
        <NewsCardLoadSkeleton
          key={`most-visited-skeleton-${index}`}
        />
      ))
    ) : showMostVisitedStatus ? (
      <div className="status-message">
        {visibleMostVisitedError || "خبری برای این بازه پیدا نشد"}
      </div>
    ) : isMostVisitedMode && isMobile ? (
      <MostVisitedPager
        pages={mostVisitedPages}
        favicons={favicons}
        onNewsOpen={openMostVisitedNewsWithoutRecording}
        activeFilter={mostVisitedFilter}
        onFilterChange={handleMostVisitedFilterChange}
        scrollToTopSignal={mostVisitedScrollToTopSignal}
        onActivePageAtTopChange={setIsMostVisitedPageAtTop}
        savedLinks={savedLinks}
        onToggleSaved={toggleSavedNews}
        onShare={handleShareNews}
        onView={handleMostVisitedViewNews}
        target={mostVisitedTarget}
        onTargetHandled={() => setMostVisitedTarget(null)}
      />
    ) : isMostVisitedMode ? (
      activeMostVisitedItems.map((item) => (
        <NewsCard
          key={item.link}
          news={item}
          favicon={favicons[item.source]}
          onOpen={openMostVisitedNewsWithoutRecording}
          isSaved={savedLinks?.has(item.link)}
          onToggleSaved={toggleSavedNews}
          onShare={handleShareNews}
          onView={handleMostVisitedViewNews}
        />
      ))
    ) : null;

  /*
  | Lock vertical scrolling only while the requested full-page skeletons are
  | visible:
  |
  | 1. Home feed
  | 2. Desktop Focus feed
  | 3. Most Visited feed
  |
  | Real news feeds regain their normal scrolling as soon as loading finishes.
  */
  const isSkeletonScrollLocked =
    (isHomeLayerActive && showHomeFeedSkeleton) ||
    (isDesktopFocusFeedMode && showDesktopFocusSkeleton) ||
    (
      isMostVisitedMode &&
      showMostVisitedStatus &&
      (isActiveMostVisitedLoading || !hasLoadedActiveMostVisited) &&
      !visibleMostVisitedError
    );

  const savedFeedView = isSavedMode && isMobile ? (
    <SavedPager
      pages={savedPages}
      favicons={favicons}
      onMarkAsRead={markAsRead}
      onNewsOpen={recordNewsView}
      activeFilter={savedFilter}
      onFilterChange={handleSavedFilterChange}
      scrollToTopSignal={savedScrollToTopSignal}
      onActivePageAtTopChange={setIsSavedPageAtTop}
      savedLinks={savedLinks}
      onToggleSaved={toggleSavedNews}
      onShare={handleShareNews}
      onView={handleViewNews}
    />
  ) : showSavedStatus ? (
    <div className="status-message">خبری ذخیره نشده است</div>
  ) : isSavedMode ? (
    <VirtualizedNewsList
      items={activeSavedItems}
      favicons={favicons}
      onMarkAsRead={markAsRead}
      onNewsOpen={recordNewsView}
      scrollParent={
        (isMobile ? contentScrollParent : mainScrollParent) ||
        undefined
      }
      savedLinks={savedLinks}
      onToggleSaved={toggleSavedNews}
      onShare={handleShareNews}
      onView={handleViewNews}
    />
  ) : null;

  const activeModeFeedView = isMostVisitedMode
    ? mostVisitedFeedView
    : isSavedMode
    ? savedFeedView
    : isMobile && focusSource !== null
    ? mobileFocusFeedView
    : null;

  return (
    <>
      {showSharedNewsLoadingTicker ? (
        <LoadingTicker
          ariaLabel="در حال بارگذاری خبر"
          text="در حال بارگذاری خبر"
        />
      ) : (
        showInitialAppSkeleton &&
        !showConnectionTicker &&
        <LoadingTicker />
      )}

      {!showSharedNewsLoadingTicker &&
        !showConnectionTicker &&
        showNoSourcesTicker && <LoadingTicker variant="noSources" />}

      {!showSharedNewsLoadingTicker && showConnectionTicker && (
        <LoadingTicker
          variant="error"
          ariaLabel={connectionTickerText}
          text={connectionTickerText}
        />
      )}

      {!showSharedNewsLoadingTicker &&
        !showInitialAppSkeleton &&
        !showConnectionTicker &&
        isSavedMode && (
          <TickerSavedBar
            activeFilter={savedFilter}
            onFilterChange={handleSavedFilterChange}
          />
        )}

      {!showSharedNewsLoadingTicker &&
        !showInitialAppSkeleton &&
        !showConnectionTicker &&
        !isSavedMode &&
        (visibleTickerNews.length > 0 || focusSource !== null) && (
          <Ticker
            news={visibleTickerNews}
            focusNews={focusTickerNews}
            onNewsClick={ensureAndScroll}
            isFocusMode={focusSource !== null}
            focusSource={focusSource}
            activeSources={activeSources || []}
            favicons={favicons}
            isModalOpen={isMostVisitedMode}
            onOpenModal={isSavedMode ? handleSavedTickerClick : openMostVisitedMode}
            isMobile={isMobile}
            isSourcePanelOpen={isSourcePanelOpen}
            onFocusSourceChange={handlePagerSourceChange}
            showDesktopFocusSkeleton={showDesktopFocusTickerSkeleton}
          />
        )}

      {!showSharedNewsLoadingTicker && isMostVisitedMode && (
        <TickerMostVisitedBar
          activeFilter={mostVisitedFilter}
          onFilterChange={handleMostVisitedFilterChange}
          className={
            focusSource !== null ? "ticker-most-visited-bar--focus" : ""
          }
        />
      )}

      <div
        className={`app${
          showInitialAppSkeleton ? " app--loading" : ""
        }`}
      >
        <div className="app-body">
          <div
            className={`app-content-wrapper${
              isSourcePanelOpen ? " app-content-wrapper--panel-open" : ""
            }${
              showSourcePanelOnly
                ? " app-content-wrapper--source-panel-only"
                : ""
            }${
              isSkeletonScrollLocked
                ? " app-content-wrapper--skeleton-scroll-locked"
                : ""
            }`}
            ref={setContentWrapperElement}
          >
            {showInitialAppSkeleton && (
              <div className="initial-app-skeleton-overlay">
                <AppSkeleton />
              </div>
            )}

            {availableSources.length > 0 &&
              !(isSavedMode && !isMobile) && (
                <div
                  className={`persistent-source-navigation${
                    !isMobile && isMostVisitedMode
                      ? " persistent-source-navigation--frozen"
                      : ""
                  }`}
                  aria-hidden={
                    !isMobile && isMostVisitedMode ? true : undefined
                  }
                >
                  {!showSourcePanelOnly && (
                    <SourceQuickAccess
                      allSources={quickAccessSources}
                      activeSources={activeSources || []}
                      focusSource={focusSource}
                      onSourceFocus={handleSourceFocus}
                      favicons={favicons}
                      badgeCounts={unreadCounts}
                      isSourcePanelOpen={isSourcePanelOpen}
                      onToggleSourcePanel={handleSourcePanelToggle}
                      isFocusMode={
                        focusSource !== null || isMostVisitedMode || isSavedMode
                      }
                    />
                  )}

                  <SourcePanel
                    sources={availableSources}
                    activeSources={activeSources || []}
                    onToggle={handleToggleSource}
                    favicons={favicons}
                    searchTerm={sourceSearchTerm}
                    onSearchChange={setSourceSearchTerm}
                    isMobileOpen={isSourcePanelOpen}
                    onMobileClose={() => {
                      if (!hasNoSelectedSources) setIsSourcePanelOpen(false);
                    }}
                    canMobileClose={!hasNoSelectedSources}
                  />
                </div>
              )}

            {!hasNoSelectedSources && (
              <main
                className={`app-main${
                  focusSource !== null || isSavedMode
                    ? " app-main--no-quick-access"
                    : ""
                }${
                  isSkeletonScrollLocked
                    ? " app-main--skeleton-scroll-locked"
                    : ""
                }`}
                ref={setMainElement}
              >
                {!isMobile &&
                  focusSource === null &&
                  !isMostVisitedMode &&
                  !isSavedMode && (
                    <FloatingNewsCard
                      pendingNews={pendingHomeState.freshNews}
                      sourceNames={pendingFreshPresentation.sources}
                      freshCount={pendingFreshPresentation.count}
                      favicons={favicons}
                      onShow={handleShowHeldNews}
                    />
                  )}

                <div
                    className={`news-grid${
                      isMobile &&
                      focusSource !== null &&
                      !isMostVisitedMode &&
                      !isSavedMode
                        ? " news-grid--focus-pager"
                        : ""
                    }${
                      !isMostVisitedMode && !isSavedMode
                        ? " news-grid--virtualized"
                        : ""
                    }${isMostVisitedMode ? " news-grid--most-visited" : ""}${
                      isSavedMode ? " news-grid--saved" : ""
                    }`}
                  >
                    <div className="persistent-feed-stack">
                      {homeFeedView}

                      {desktopFocusFeedView}

                      {activeModeFeedView && (
                        <div className="persistent-mode-feed">
                          {activeModeFeedView}
                        </div>
                      )}
                    </div>
                  </div>
              </main>
            )}
          </div>
        </div>

        {!showInitialAppSkeleton &&
          !hasNoSelectedSources &&
          !(isMobile && isSourcePanelOpen) && (
            <BottomFloatingCapsule
              isFocusMode={focusSource !== null}
              onScrollToTop={scrollToTop}
              onReadAll={handleBottomMarkAllRead}
              onClearSaved={clearSavedNews}
              onExitFocus={handleExitFocus}
              isAtTop={
                isMostVisitedMode
                  ? isMobile
                    ? isMostVisitedPageAtTop
                    : isAtTop
                  : isSavedMode && isMobile
                  ? isSavedPageAtTop
                  : isMobile &&
                    focusSource !== null &&
                    !isMostVisitedMode &&
                    !isSavedMode
                  ? isFocusPageAtTop
                  : isAtTop
              }
              totalUnreadCount={totalUnreadCount}
              focusedUnreadCount={focusedUnreadCount}
              isModalOpen={isMostVisitedMode}
              isMostVisitedMode={isMostVisitedMode}
              onCloseModal={closeMostVisitedMode}
              isSavedMode={isSavedMode}
              onOpenSaved={openSavedMode}
              onCloseSaved={closeSavedMode}
              savedCount={savedNews.length}
              middleComponent={pendingCapsule}
            />
          )}
      </div>

      <PopupNotice
        popup={popupNotice.popup}
        visible={popupNotice.visible}
        onClose={popupNotice.close}
        onClick={popupNotice.click}
      />

      {shouldShowSharedNewsModal && (
        <SharedNewsModal
          news={sharedNews}
          favicon={sharedNews ? favicons[sharedNews.source] : null}
          isLoading={false}
          error={sharedNewsError}
          onClose={closeSharedNews}
          onOpenOriginal={
            sharedNewsSuppressViewIncrement
              ? openMostVisitedNewsWithoutRecording
              : recordNewsView
          }
        />
      )}
    </>
  );
}

export default App;
